import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { switchMap, of } from 'rxjs';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { StrategyCatalogService } from '../../services/strategy-catalog.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import {
  CUSTOM_STRATEGY_CATEGORIES,
  CUSTOM_STRATEGY_KPIS,
  CUSTOM_STRATEGY_TAGS,
  CUSTOM_STRATEGY_PREVIEW_MAX_SIZE,
  CUSTOM_STRATEGY_PREVIEW_ALLOWED_TYPES,
  CreateCustomStrategyPayload,
} from '../../strategy-catalog.models';

@Component({
  selector: 'create-custom-strategy-dialog',
  standalone: false,
  templateUrl: './create-custom-strategy-dialog.component.html',
  styleUrl: './create-custom-strategy-dialog.component.scss',
})
export class CreateCustomStrategyDialogComponent {
  titleControl = new FormControl<string>('', [Validators.required]);
  categoryControl = new FormControl<string>('', [Validators.required]);
  conceptControl = new FormControl<string>('', [Validators.required]);

  categoryOptions: DropdownOption[] = CUSTOM_STRATEGY_CATEGORIES.map((c) => ({ label: c, value: c }));
  kpiOptions: DropdownOption[] = CUSTOM_STRATEGY_KPIS.map((k) => ({ label: k, value: k, checked: false }));
  tagOptions: DropdownOption[] = CUSTOM_STRATEGY_TAGS.map((t) => ({ label: t, value: t, checked: false }));
  selectedKpis: string[] = [];
  selectedTags: string[] = [];
  tagSearchValue = '';
  previewFile: File | null = null;
  previewUrl: string | null = null;
  submitting = false;
  submitted = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    private dialogRef: MatDialogRef<CreateCustomStrategyDialogComponent>,
    private strategyCatalogService: StrategyCatalogService,
    private toastService: ToastService,
  ) {}

  onCategoryChange(option: DropdownOption): void {
    this.categoryControl.setValue(option.value as string);
  }

  onKpiChange(options: DropdownOption[]): void {
    this.selectedKpis = options.filter((o) => o.checked).map((o) => o.value as string);
  }

  onTagChange(options: DropdownOption[]): void {
    this.selectedTags = options.filter((o) => o.checked).map((o) => o.value as string);
  }

  onTagSearch(value: string): void {
    this.tagSearchValue = value;
  }

  addTag(): void {
    const tag = this.tagSearchValue.trim();
    if (!tag || this.tagOptions.some((t) => t.value === tag)) return;
    this.tagOptions = [...this.tagOptions, { label: tag, value: tag, checked: true }];
    this.selectedTags = [...this.selectedTags, tag];
    this.tagSearchValue = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!CUSTOM_STRATEGY_PREVIEW_ALLOWED_TYPES.includes(file.type)) {
      this.toastService.showToast({ message: 'Only PNG, JPEG, and WebP images are supported', mode: 'danger' });
      return;
    }
    if (file.size > CUSTOM_STRATEGY_PREVIEW_MAX_SIZE) {
      this.toastService.showToast({ message: 'Image must be under 2MB', mode: 'danger' });
      return;
    }

    this.previewFile = file;
    const reader = new FileReader();
    reader.onload = () => (this.previewUrl = reader.result as string);
    reader.readAsDataURL(file);
    input.value = '';
  }

  removePreview(): void {
    this.previewFile = null;
    this.previewUrl = null;
  }

  close(): void {
    this.dialogRef.close();
  }

  get isFormValid(): boolean {
    return this.titleControl.valid && this.categoryControl.valid && this.conceptControl.valid;
  }

  submit(): void {
    this.submitted = true;
    this.titleControl.markAsTouched();
    this.categoryControl.markAsTouched();
    this.conceptControl.markAsTouched();
    if (!this.isFormValid || this.submitting) return;
    this.submitting = true;

    const upload$ = this.previewFile ? this.strategyCatalogService.uploadPreviewImage(this.previewFile) : of(null);

    upload$
      .pipe(
        switchMap((uploadResult) => {
          const payload: CreateCustomStrategyPayload = {
            title: this.titleControl.value,
            category: this.categoryControl.value,
            concept: this.conceptControl.value || '',
            kpiTargets: this.selectedKpis,
            tags: this.selectedTags,
            ...(uploadResult && { previewImageKey: uploadResult.key }),
          };
          return this.strategyCatalogService.createCustomStrategy(payload);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.submitting = false;
          this.toastService.showToast({ message: 'Custom strategy created', mode: 'success' });
          this.dialogRef.close(result);
        },
        error: () => {
          this.submitting = false;
          this.toastService.showToast({ message: 'Failed to create custom strategy', mode: 'danger' });
        },
      });
  }
}
